/** @file Document-space deform offsets of a mesh at a frame, sampled from its keys exactly as core does. */
import type { DeformKey } from '@pose2d/format/types';
import { curveProgress } from './curveProgress';

/** out[start + j] += w · values[j] inside out (a null key is the rest shape and adds nothing). */
function addKey(out: number[], key: DeformKey | undefined, w: number): void {
  const o = key?.offsets;
  if (o === null || o === undefined || w === 0) return;
  for (let j = 0; j < o.values.length && o.start + j < out.length; j++) out[o.start + j] = (out[o.start + j] ?? 0) + w * (o.values[j] ?? 0);
}

/**
 * `size` offsets (2 per vertex): before the first key the first key, at or after the last the last, between two keys
 * (1 − f)·k0 + f·k1 with f the 0..1 progress of k0's curve (core's deform sampler, keys ascending by frame).
 */
export function deformAtFrame(keys: readonly DeformKey[] | undefined, frame: number, size: number): number[] {
  const out = new Array<number>(size).fill(0);
  if (keys === undefined || keys.length === 0) return out;
  let i = -1;
  while (i + 1 < keys.length && (keys[i + 1]?.f ?? Infinity) <= frame) i++;
  const k0 = keys[Math.max(i, 0)];
  const k1 = keys[i + 1];
  if (i < 0 || k0 === undefined || k1 === undefined) {
    addKey(out, k0, 1);
    return out;
  }
  const f = curveProgress(k0.curve, (frame - k0.f) / (k1.f - k0.f));
  addKey(out, k0, 1 - f);
  addKey(out, k1, f);
  return out;
}
